export const MATCH_WEIGHT_MIN = 1;
export const MATCH_WEIGHT_MAX = 100;

export function clampMatchWeight(value: number | null | undefined): number {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n) || n < MATCH_WEIGHT_MIN) return MATCH_WEIGHT_MIN;
  return Math.min(n, MATCH_WEIGHT_MAX);
}

export function isValidMatchWeightInput(value: string | number): boolean {
  const parsed = typeof value === "string" ? Number(value) : value;
  return (
    Number.isInteger(parsed) &&
    parsed >= MATCH_WEIGHT_MIN &&
    parsed <= MATCH_WEIGHT_MAX
  );
}

export function resolveParticipantMatchWeight(params: {
  matchWeight?: number | null;
  groupId?: string | null;
  groupSize?: number | null;
}): number {
  const weight = clampMatchWeight(params.matchWeight ?? 1);
  if (params.groupId != null && params.groupId !== "") {
    return weight * Math.max(params.groupSize ?? 1, 1);
  }
  return weight;
}

/**
 * Admin add-volunteer weight: explicit value wins; default 1 skips the prompt;
 * otherwise prompt and clamp. Cancel is represented as null from `prompt`.
 */
export async function resolveMatchWeightForAdd(params: {
  opportunityId: string;
  defaultMatchWeight?: number | null;
  matchWeight?: number | null;
  prompt: (initial: number) => Promise<number | null | undefined>;
  loadDefaultMatchWeight?: (
    opportunityId: string,
  ) => Promise<number | null | undefined>;
  inFlight?: Map<string, Promise<number | undefined>>;
}): Promise<number | undefined> {
  if (params.matchWeight != null) {
    return clampMatchWeight(params.matchWeight);
  }

  const inFlight = params.inFlight ?? defaultMatchWeightInFlight;
  const existing = inFlight.get(params.opportunityId);
  if (existing) return existing;

  const pending = (async () => {
    let defaultWeight = params.defaultMatchWeight;
    if (defaultWeight == null && params.loadDefaultMatchWeight) {
      defaultWeight = await params.loadDefaultMatchWeight(params.opportunityId);
    }
    const clampedDefault = clampMatchWeight(defaultWeight ?? 1);
    if (clampedDefault === MATCH_WEIGHT_MIN) return undefined;

    const prompted = await params.prompt(clampedDefault);
    if (prompted == null) {
      throw new MatchWeightPromptCancelled();
    }
    return clampMatchWeight(prompted);
  })();

  inFlight.set(params.opportunityId, pending);
  try {
    return await pending;
  } finally {
    inFlight.delete(params.opportunityId);
  }
}

export class MatchWeightPromptCancelled extends Error {
  constructor() {
    super("MATCH_WEIGHT_PROMPT_CANCELLED");
    this.name = "MatchWeightPromptCancelled";
  }
}

const defaultMatchWeightInFlight = new Map<string, Promise<number | undefined>>();
